import { useState, type CSSProperties } from 'react';
import { IconCheck, IconCopy, IconGlobe, IconLink, IconLock } from './icons';
import { MenuAnchor, MenuSurface, useDismiss } from './Menu';

/**
 * The design's three levels (PRODUCT_RULES: only collections and studies
 * carry visibility). Server names: private = workspace, link = unlisted,
 * public = public.
 */
export type Visibility = 'private' | 'link' | 'public';

export const VIS_RANK: Record<Visibility, number> = { private: 0, link: 1, public: 2 };
export const VIS_LABEL: Record<Visibility, string> = { private: 'Private', link: 'Shared', public: 'Public' };
export const VIS_HINT: Record<Visibility, string> = {
  private: 'Only members of this workspace',
  link: 'Only people with the link can view',
  public: 'Published publicly. Anyone can find it',
};
export const VIS_TITLE: Record<Visibility, string> = { private: 'Private', link: 'Shared with link', public: 'Public' };

/**
 * Does an item inside a container grant something the container does not?
 * Shared and Public grant different things (decided 2026-09-15): Shared opens
 * with a link and no account, Public can be found by anyone. So a Shared
 * study inside a Public collection is flagged too: its link opens it without
 * an account, which the collection does not.
 */
export function grantsMore(item: Visibility, container: Visibility): boolean {
  return item !== 'private' && item !== container;
}

export interface InheritedNote {
  vis: 'link' | 'public';
  title: string;
  detail: string;
}

/**
 * What a study's menu says when its collection exposes it more than its own
 * setting does, or at all. A link grants everything inside its item, so a
 * Shared collection's link opens every study in it; a Public collection
 * publishes every study in it.
 */
export function inheritedNote(own: Visibility, collection: Visibility | null | undefined, name: string): InheritedNote | null {
  const quoted = `\u201c${name}\u201d`;
  if (collection === 'link') {
    if (own === 'link') return { vis: 'link', title: `Also shared through ${quoted}`, detail: 'The collection\u2019s link works too' };
    return { vis: 'link', title: `${own === 'private' ? 'Shared' : 'Also shared'} through ${quoted}`, detail: 'Anyone with the collection\u2019s link can view this study' };
  }
  if (collection === 'public' && own !== 'public') {
    return { vis: 'public', title: `Published publicly through ${quoted}`, detail: 'Anyone can find this study' };
  }
  return null;
}

export function maxVis(...vs: Array<Visibility | undefined | null>): Visibility {
  return vs.filter((v): v is Visibility => !!v).reduce<Visibility>((a, b) => (VIS_RANK[b] > VIS_RANK[a] ? b : a), 'private');
}

export function toServerVisibility(v: Visibility): 'workspace' | 'unlisted' | 'public' {
  return v === 'private' ? 'workspace' : v === 'link' ? 'unlisted' : 'public';
}
export function fromServerVisibility(v: string | null | undefined): Visibility {
  return v === 'public' ? 'public' : v === 'unlisted' ? 'link' : 'private';
}

export function VisIcon({ vis, size = 12, sw = 2.2 }: { vis: Visibility; size?: number; sw?: number }) {
  if (vis === 'link') return <IconLink size={size} sw={sw} />;
  if (vis === 'public') return <IconGlobe size={size} sw={sw} />;
  return <IconLock size={size} sw={sw} />;
}

/** The 22px badge on Mosaic/Stack thumbs (bottom right). */
export function VisBadge({ vis }: { vis: Visibility }) {
  const priv = vis === 'private';
  return (
    <div
      title={VIS_TITLE[vis]}
      style={{
        position: 'absolute',
        bottom: 10,
        right: 10,
        width: 22,
        height: 22,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 5,
        background: priv ? 'rgba(255,255,255,.9)' : 'rgba(15,15,16,.78)',
        color: priv ? '#3f3f44' : '#fff',
      }}
    >
      <VisIcon vis={vis} />
    </div>
  );
}

/** Inline "icon + label" used in list rows (13px #6b6b70). */
export function VisInline({ vis }: { vis: Visibility }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--tr-muted)' }}>
      <VisIcon vis={vis} />
      {VIS_LABEL[vis]}
    </div>
  );
}

/**
 * The 22px visibility chip, with its menu of the three options (hint, check on
 * the current one, "Copy share link" / "Copy public link" and "Reset link"
 * under the current non-private option).
 *
 * `readOnly` is a viewer's: the chip says what the item is and opens nothing,
 * since only admins and editors change visibility or hand out its link.
 * `inherited` is what the item's collection adds (inheritedNote), shown above
 * the options so the owner knows the item's own setting is not the whole story.
 */
export function VisChip({
  vis,
  onChange,
  onCopy,
  onReset,
  inherited,
  readOnly,
  style,
}: {
  vis: Visibility;
  onChange: (v: Visibility) => void;
  onCopy: () => void;
  onReset?: () => void;
  inherited?: InheritedNote | null;
  readOnly?: boolean;
  style?: CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  useDismiss(open, () => setOpen(false));
  const priv = vis === 'private';
  const chipStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    height: 22,
    padding: readOnly ? '0 8px' : '0 6px 0 8px',
    border: `1px solid ${priv ? 'var(--tr-dash)' : 'var(--tr-ink)'}`,
    borderRadius: 5,
    background: priv ? 'var(--tr-surface)' : 'var(--tr-ink)',
    color: priv ? 'var(--tr-text-2)' : 'var(--tr-on-ink)',
    fontSize: 11.5,
    fontWeight: 500,
    fontFamily: 'inherit',
  };
  if (readOnly) {
    return (
      <div title={VIS_HINT[vis]} style={{ ...chipStyle, ...style }}>
        <VisIcon vis={vis} size={11} />
        {VIS_LABEL[vis]}
      </div>
    );
  }
  const smallButton: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    height: 26,
    padding: '0 9px',
    border: '1px solid var(--tr-line-2)',
    borderRadius: 5,
    background: 'var(--tr-surface)',
    color: 'var(--tr-ink)',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    flex: 'none',
  };
  return (
    <MenuAnchor display="flex" style={style}>
      <button
        type="button"
        title="Change who can see this"
        className="tr-hover-fade"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        style={{ ...chipStyle, cursor: 'pointer' }}
      >
        <VisIcon vis={vis} size={11} />
        {VIS_LABEL[vis]}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" style={{ opacity: 0.7 }} aria-hidden>
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open ? (
        <MenuSurface top={28} left={0} width={290}>
          {inherited ? (
            <div
              data-inherited="1"
              style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px 10px', margin: '0 0 4px', borderBottom: '1px solid var(--tr-line)' }}
            >
              <span style={{ color: 'var(--tr-text-2)', marginTop: 2, display: 'flex' }}>
                <VisIcon vis={inherited.vis} size={14} />
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.35 }}>{inherited.title}</span>
                <span style={{ fontSize: 12, color: 'var(--tr-muted)', lineHeight: 1.35 }}>{inherited.detail}</span>
              </div>
            </div>
          ) : null}
          {(['private', 'link', 'public'] as Visibility[]).map((v) => {
            const on = v === vis;
            return (
              <div
                key={v}
                className="tr-hover-track"
                onClick={(e) => {
                  e.stopPropagation();
                  if (on) return;
                  onChange(v);
                  if (v === 'private') setOpen(false);
                }}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', borderRadius: 5, cursor: 'pointer', background: on ? 'var(--tr-hover)' : 'transparent' }}
              >
                <span style={{ color: 'var(--tr-text-2)', marginTop: 2, display: 'flex' }}>
                  <VisIcon vis={v} size={14} />
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{VIS_LABEL[v]}</span>
                  <span style={{ fontSize: 12, color: 'var(--tr-muted)', lineHeight: 1.35 }}>{VIS_HINT[v]}</span>
                  {on && v !== 'private' ? (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="tr-hover-soft"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpen(false);
                          onCopy();
                        }}
                        style={smallButton}
                      >
                        <IconCopy size={12} />
                        {v === 'public' ? 'Copy public link' : 'Copy share link'}
                      </button>
                      {onReset ? (
                        <button
                          type="button"
                          className="tr-hover-soft"
                          title="Replace the link: the current one stops working"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpen(false);
                            onReset();
                          }}
                          style={{ ...smallButton, color: 'var(--tr-muted)' }}
                        >
                          Reset link
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {on ? <span style={{ color: 'var(--tr-ink)', display: 'flex', marginTop: 2 }}><IconCheck size={14} sw={2.5} color="currentColor" /></span> : null}
              </div>
            );
          })}
        </MenuSurface>
      ) : null}
    </MenuAnchor>
  );
}

export interface OverItem {
  name: string;
  kind: 'Study';
  vis: Visibility;
  mono?: boolean;
}

/**
 * The red triangle next to a container whose items are more visible than
 * it, with its explanatory popover listing them.
 */
export function OverVisibleWarning({ items, container, containerVis }: { items: OverItem[]; container: 'collection' | 'group'; containerVis: Visibility }) {
  const [open, setOpen] = useState(false);
  useDismiss(open, () => setOpen(false));
  if (!items.length) return null;
  return (
    <MenuAnchor display="flex" style={{ marginLeft: -4 }}>
      <button
        type="button"
        title="Some items inside are shared beyond this"
        className="tr-hover-danger2"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        style={{ width: 22, height: 22, display: 'grid', placeItems: 'center', border: 0, borderRadius: 5, background: 'transparent', color: '#b3261e', cursor: 'pointer' }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01" />
        </svg>
      </button>
      {open ? (
        <MenuSurface top={28} left={0} width={300} padding="12px 12px 8px" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.4 }}>
            {items.length} {items.length === 1 ? 'item is' : 'items are'} shared beyond this {container}
          </span>
          <span style={{ fontSize: 12, color: '#6b6b70', lineHeight: 1.4 }}>
            This {container} is {VIS_LABEL[containerVis].toLowerCase()}, but the items below were shared on their own. Anyone with access to them can view them.
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, margin: '2px -6px 0' }}>
            {items.map((i) => (
              <div key={i.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', borderRadius: 5, fontSize: 12.5 }}>
                <span style={{ color: '#3f3f44', display: 'flex' }}>
                  <VisIcon vis={i.vis} />
                </span>
                <span className="tr-ellipsis" style={{ fontWeight: 500, fontFamily: i.mono ? "'JetBrains Mono', monospace" : 'inherit', flex: 1 }}>
                  {i.name}
                </span>
                <span style={{ fontSize: 11.5, color: '#8a8a8f', whiteSpace: 'nowrap' }}>
                  {i.kind} · {VIS_LABEL[i.vis]}
                </span>
              </div>
            ))}
          </div>
        </MenuSurface>
      ) : null}
    </MenuAnchor>
  );
}
